/**
 * Lightweight gtag helpers for the public marketing site.
 * Never pass PII (name, email, company, message, subject) as event params.
 */

const LEAD_QUERY_ALLOWLIST = new Set(['service']);

function safeGtag(...args) {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') {
    return;
  }
  window.gtag(...args);
}

function currentPagePath() {
  if (typeof window === 'undefined') return '';
  return `${window.location.pathname}${window.location.search}`;
}

/** Path for generate_lead: pathname plus allowlisted query keys only (no PII). */
function leadPagePath() {
  if (typeof window === 'undefined') return '';
  const { pathname, search } = window.location;
  if (!search) return pathname;
  const incoming = new URLSearchParams(search);
  const safe = new URLSearchParams();
  LEAD_QUERY_ALLOWLIST.forEach((key) => {
    const value = incoming.get(key);
    if (value) safe.set(key, value);
  });
  const qs = safe.toString();
  return qs ? `${pathname}?${qs}` : pathname;
}

/**
 * GA4 recommended lead event. Call only after the contact API returns HTTP ok.
 * Allowed params: service, inquiry_type, role, company_size, page_path
 * (plus GA4 recommended currency / value / lead_source).
 */
export function trackGenerateLead({
  service = '',
  inquiryType = '',
  role = '',
  companySize = '',
} = {}) {
  safeGtag('event', 'generate_lead', {
    currency: 'USD',
    value: 0,
    lead_source: 'website_contact',
    service: service || '',
    inquiry_type: inquiryType || '',
    role: role || '',
    company_size: companySize || '',
    page_path: leadPagePath(),
  });
}

export const CONSENT_COOKIE = 'myorglab_cookie_consent';
export const GA4_MEASUREMENT_ID = 'G-SQ79FRF0H8';

/** True when the visitor previously clicked Accept on the cookie banner. */
export function hasAcceptedCookies() {
  if (typeof document === 'undefined') return false;
  return document.cookie
    .split(';')
    .some((c) => c.trim() === `${CONSENT_COOKIE}=true`);
}

export const GTAG_SCRIPT_ID = 'ga4-gtag-js';
export const GTAG_SCRIPT_SRC = `https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`;

/**
 * Basic Consent Mode: gtag.js is not in index.html, so a visitor who has not
 * accepted (or who declined) never loads it and sends no Google requests.
 * Injects the library once; calls queued in dataLayer replay when it loads.
 */
export function loadGtagScript() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(GTAG_SCRIPT_ID)) return;
  if (document.querySelector('script[src^="https://www.googletagmanager.com/gtag/js"]')) return;
  const script = document.createElement('script');
  script.id = GTAG_SCRIPT_ID;
  script.async = true;
  script.src = GTAG_SCRIPT_SRC;
  document.head.appendChild(script);
}

/**
 * Consent Mode v2: grant all four signals, then load gtag.js (once).
 * Called on Accept or when a returning visitor's accepted grant is restored.
 */
export function grantConsent() {
  safeGtag('consent', 'update', {
    analytics_storage: 'granted',
    ad_storage: 'granted',
    ad_user_data: 'granted',
    ad_personalization: 'granted',
  });
  loadGtagScript();
}

/**
 * The only page_view source. index.html sets send_page_view:false, and
 * useSpaPageViews calls this once per route, only after consent is granted.
 */
export function trackPageView(pagePath) {
  const path = pagePath || currentPagePath();
  if (!path) return;
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  safeGtag('event', 'page_view', {
    page_path: path,
    ...(origin ? { page_location: `${origin}${path}` } : {}),
  });
}
